import {
    AppBar,
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    styled,
    Toolbar,
    Tooltip
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import { LocalStorageConstants } from "../../../shared/constants/LocalStorageConstants";
import { StatusInfoPanel } from "../components/status-info-panel/StatusInfoPanel";
import MenuIcon from '@mui/icons-material/Menu';
import { configurationService } from "../../../appConfigService";
import axios from "axios";
import { BaseAssetDto } from "../../../shared/types/BaseAssetDto";
import { useNavigate } from "react-router-dom";
import {
    UnderlyingAssetSelector
} from "../../options-table/components/underlying-asset-selector/UnderlyingAssetSelector";
import { PortfolioSelectorView } from "../../portfolio-info/views/portfolio-selector-view/PortfolioSelectorView";
import { DataProviders } from "../../../shared/types/DataProviders";
import { useAuthContext } from "../../authentication/hooks/use-auth/AuthContext";
import BackupIcon from '@mui/icons-material/Backup';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import { ComparePortfoliosDialog } from "../../portfolio-info/components/ComparePortfoliosDialog";
import { ApplicationSettingsDialog } from "../../../screens/application-header/dialogs/ApplicationSettingsDialog";

export function ApplicationHeader() {
    const {
        marketDataDto,
        fetchMarketData,
        selectedDataProvider,
        setSelectedDataProvider,
    } = useAppContext();
    
    const { userAuthData, isUserAuthenticated, logout } = useAuthContext();

    useEffect(() => {
        setIsDataUpdating(true);
        fetchMarketInfo().then(() => setIsDataUpdating(false)).finally(() => setIsDataUpdating(false));
    }, []);
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    
    const [isDataUpdating, setIsDataUpdating] = useState<boolean>(false);
    
    const [isCurrentExchangeDataSaving, setIsCurrentExchangeDataSaving] = useState<boolean>(false);
    const [lastDataUpdateTimestamp, setLastDataUpdateTimestamp] = useState<Date | null>(null);

    const navigate = useNavigate();

    const fetchMarketInfo = async () => {
        const selectedBaseAssetSymbol = localStorage.getItem(LocalStorageConstants.SelectedBaseAssetSymbol);
        const selectedExpiration = localStorage.getItem(LocalStorageConstants.SelectedExpirationName);

        const result = await fetchMarketData(selectedBaseAssetSymbol, selectedExpiration);
        const lastDataUpdateTimestamp = result?.LastDataUpdateTimestamp ?? null;
        setLastDataUpdateTimestamp(lastDataUpdateTimestamp);
    }
    
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };
    
    async function handleLoadExchangeData(dataProvider: DataProviders) {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        setIsDataUpdating(true);
        setSelectedDataProvider(dataProvider);
        handleClose();
        await fetchMarketInfo();
        setIsDataUpdating(false);
    }
    
    function handleSaveCurrentExchangeDataToSnapshot() {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        setIsCurrentExchangeDataSaving(true);
        
        axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .put<boolean>(
                `api/market-data/exchange-data-snapshot`,
                selectedDataProvider,
                {
                    headers: {
                        ...headerValues.appJson,
                        Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                    },
                },
            )
            .then(async () => {
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {
                setIsCurrentExchangeDataSaving(false);
            });

        handleClose();
    }
    
    const handleOpenApplicationSettingsClick = () => {
        setIsApplicationSettingsDialogOpen(true);
    }
    
    const [selectedBaseAsset1, setSelectedBaseAsset1] = useState<BaseAssetDto | null>(null);

    const appHeaderMainColor = '#FACC2D';
    const secondaryHoverColour = '#FFFFFF';

    const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

    const [isApplicationSettingsDialogOpen, setIsApplicationSettingsDialogOpen] = useState<boolean>(false);
    
    return (
        <Box>
            <ApplicationSettingsDialog
                isOpen={isApplicationSettingsDialogOpen}
                setIsOpen={setIsApplicationSettingsDialogOpen}
                icon={<SettingsIcon/>}
            />
            <AppBar position="fixed" sx={{ bgcolor: '#22293A' }}>
                <Toolbar>
                    <Tooltip title="Application menu">
                        <IconButton
                            onClick={handleClick}
                            sx={{ color: appHeaderMainColor, p: 0, ml: -2, pr: 2 }}
                            edge="start"
                            aria-controls={open ? 'account-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                        >
                            <MenuIcon sx={{width: 48, height: 48}}/>
                        </IconButton>
                    </Tooltip>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-end"
                        sx={{color: appHeaderMainColor, flexGrow: 1 }}
                    >
                        <UnderlyingAssetSelector
                            marketDataDto={marketDataDto}
                            setValue={setSelectedBaseAsset1}
                            fetchMarketData={fetchMarketData}/>
                        <PortfolioSelectorView />
                        <Stack
                            direction="row"
                            spacing={0}
                            sx={{
                                transform: 'translate(0,2px)'
                            }}
                        >
                            <Tooltip
                                title={
                                    selectedDataProvider === DataProviders.Binance
                                        ? `Update client data (Socket: ${marketDataDto?.ExchangeDataWebSocketState ?? 'N/A'})`
                                        : `Load real time data`
                                }
                                slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                            >
                                <IconButton
                                    sx={{
                                        color: appHeaderMainColor,
                                        padding: '0px',
                                        marginRight: '3px',
                                        '&:hover': {
                                            color: secondaryHoverColour,
                                        },
                                        transform: 'translate(0,2px)'
                                    }}
                                    onClick={async () => await handleLoadExchangeData(DataProviders.Binance)}
                                    {...(isDataUpdating //todo: uncomment when the bug #2271 will be fixed // || selectedDataProvider !== DataProviders.Binance
                                        ? { disabled: true }
                                        : {})}
                                >
                                    {selectedDataProvider === DataProviders.Binance
                                        ? <RefreshIcon
                                                sx={{
                                                    fontSize: 44,
                                                    color: isDataUpdating || lastDataUpdateTimestamp === null ? '#ff5050' : ''
                                                }}
                                          />
                                        : <CandlestickChartIcon sx={{fontSize: 44, color: 'rgba(226,255,219,0.67)' }}/>
                                    }
                                </IconButton>
                            </Tooltip>
                            <StatusInfoPanel
                                lastDataUpdateTimestamp={lastDataUpdateTimestamp}
                                isDataUpdating={isDataUpdating}
                                spotsConnection={marketDataDto?.SpotsConnection ?? null}
                                futuresConnection={marketDataDto?.FuturesConnection ?? null}
                                optionsConnection={marketDataDto?.OptionsConnection ?? null}
                                optionsOrderBookConnections={marketDataDto?.OptionsOrderBookConnections ?? null}
                            />
                        </Stack>
                    </Stack>
                    <Menu
                        anchorEl={anchorEl}
                        id="account-menu"
                        open={open}
                        onClose={handleClose}
                        onClick={handleClose}
                        sx={
                            {
                                mt: "1px", "& .MuiMenu-paper": {backgroundColor: '#c8ddff'}
                            }
                        }
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: -1,
                                ml: -0.4,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: -100,
                                    right: 0,
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                }
                            }
                        }}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        <MenuItem
                            onClick={async () => await handleLoadExchangeData(DataProviders.Snapshot)}
                            sx={{
                                pl: 1,
                                fontSize: '15px'
                            }}
                            {...(isDataUpdating // || selectedDataProvider !== DataProviders.Binance
                                ? { disabled: true }
                                : {})}
                        >
                            <CloudDownloadIcon fontSize='small' sx={{mr: 1}}/>
                            Load snapshot data
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleSaveCurrentExchangeDataToSnapshot()}
                            sx={{
                                pl: 1,
                                fontSize: '15px'
                            }}
                            {...(isCurrentExchangeDataSaving || selectedDataProvider !== DataProviders.Binance
                                ? { disabled: true }
                                : {})}
                        >
                            <BackupIcon fontSize='small' sx={{mr: 1}}/>
                            Save real time data to snapshot
                        </MenuItem>
                    </Menu>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{marginRight: '-15px'}}
                    >
                        <Tooltip
                            title="Application settings"
                            slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                        >
                            <IconButton
                                sx={{
                                    color: appHeaderMainColor,
                                    padding: '0px',
                                    '&:hover': {
                                        color: secondaryHoverColour,
                                    },
                                }}
                                onClick={handleOpenApplicationSettingsClick}
                            >
                                <SettingsIcon sx={{ fontSize: 36, marginTop: '10px' }} />
                            </IconButton>
                        </Tooltip>                        
                        <Stack
                            direction="column"
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    marginBottom: '-8px',
                                    fontSize: '15px'
                                }}
                            >
                                {userAuthData?.userName ?? null}
                            </Box>
                            <Button
                                color="inherit"
                                sx={{
                                    color: appHeaderMainColor,
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    marginBottom: '-15px'
                                }}
                                onClick={logout}
                            >
                                Logout
                            </Button>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>
            <Offset />
        </Box>
    )
}

const headerValues = { appJson: { 'Content-Type': 'application/json' } };
